<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const loaApplications = [
  {
    id: 'LOA-2024-001',
    project: 'Mangrove Restoration Initiative – Borneo',
    pcnRef: 'PCN-2024-0042',
    submittedToDna: '2024-03-15',
    dnaAuthority: 'Ministry of Environment and Forestry',
    country: 'Indonesia',
    status: 'Under Review',
    statusColor: 'yellow',
    lastUpdate: '2024-04-01',
    notes: 'DNA requested clarification on stakeholder consultation records.',
  },
  {
    id: 'LOA-2024-002',
    project: 'Solar Park Beta – Northern Highlands',
    pcnRef: 'PCN-2024-0031',
    submittedToDna: '2024-01-10',
    dnaAuthority: 'National Climate Change Authority',
    country: 'Kenya',
    status: 'Approved',
    statusColor: 'green',
    lastUpdate: '2024-02-28',
    notes: 'LoA issued. Proceed to PIN submission.',
  },
  {
    id: 'LOA-2024-003',
    project: 'Forest Conservation Pilot – Amazon Basin',
    pcnRef: 'PCN-2023-0088',
    submittedToDna: '2023-11-05',
    dnaAuthority: 'Secretaria de Meio Ambiente',
    country: 'Brazil',
    status: 'Pending Submission',
    statusColor: 'gray',
    lastUpdate: '2023-11-05',
    notes: 'Draft PCN saved. DNA submission not yet made.',
  },
]

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-800',
    green: 'bg-green-100 text-green-800',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Host Country LoA Status</span>
        </nav>

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">Host Country LoA Status</h1>
                <p class="text-gray-600">
                  Track the status of your Letter of Authorization (LoA) applications with host country Designated National Authorities (DNAs). An approved LoA is required before proceeding to PIN submission.
                </p>
              </div>
              <button class="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 flex items-center">
                <i class="fas fa-plus mr-2"></i>New PCN
              </button>
            </div>
          </div>
        </section>

        <!-- Summary stats -->
        <div class="grid grid-cols-3 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-accent">3</p>
            <p class="text-sm text-gray-600">Total Applications</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-green-600">1</p>
            <p class="text-sm text-gray-600">LoA Approved</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-yellow-600">1</p>
            <p class="text-sm text-gray-600">Under DNA Review</p>
          </div>
        </div>

        <!-- Applications list -->
        <section class="space-y-4">
          <div
            v-for="app in loaApplications"
            :key="app.id"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div class="flex items-start justify-between mb-4">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-semibold text-accent">{{ app.project }}</h3>
                  <span :class="['inline-flex px-2 py-1 rounded-full text-xs font-medium', statusClass(app.statusColor)]">
                    {{ app.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">PCN Ref: {{ app.pcnRef }} &nbsp;·&nbsp; LoA Ref: {{ app.id }}</p>
              </div>
              <div class="text-right text-sm text-gray-500">
                <p>Last updated</p>
                <p class="font-medium text-gray-700">{{ app.lastUpdate }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
              <div>
                <p class="text-gray-500">DNA Authority</p>
                <p class="font-medium text-gray-800">{{ app.dnaAuthority }}</p>
              </div>
              <div>
                <p class="text-gray-500">Host Country</p>
                <p class="font-medium text-gray-800">{{ app.country }}</p>
              </div>
              <div>
                <p class="text-gray-500">Submitted to DNA</p>
                <p class="font-medium text-gray-800">{{ app.submittedToDna }}</p>
              </div>
            </div>

            <div class="bg-gray-50 rounded-md px-4 py-3 text-sm text-gray-700 mb-4">
              <i class="fas fa-comment-alt text-gray-400 mr-2"></i>{{ app.notes }}
            </div>

            <div class="flex items-center space-x-3">
              <button class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                <i class="fas fa-eye mr-1"></i>View PCN
              </button>
              <button v-if="app.statusColor === 'green'" class="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                <i class="fas fa-download mr-1"></i>Download LoA Certificate
              </button>
              <button v-if="app.statusColor === 'yellow'" class="px-4 py-1.5 border border-primary text-primary rounded-md text-sm hover:bg-primary/5">
                <i class="fas fa-upload mr-1"></i>Upload Response
              </button>
            </div>
          </div>
        </section>

        <!-- Info box -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
          <p class="text-sm font-medium text-blue-800 mb-1">
            <i class="fas fa-info-circle mr-2"></i>What is a Letter of Authorization (LoA)?
          </p>
          <p class="text-sm text-blue-700">
            Under Article 6.4, the host country must formally authorize your project and confirm that corresponding adjustments will be applied to any Internationally Transferred Mitigation Outcomes (ITMOs). The LoA is issued by the host country's Designated National Authority (DNA) after reviewing your PCN.
          </p>
        </div>

      </div>
    </main>
  </div>
</template>
